import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, Share2 } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import { storeShareLink } from '@mlm/shared';
import {
  getMe,
  getPackageLinks,
  getReferralCode,
  getReplicatedPage,
  getSlotShareLinks,
  getTenantConfig,
  listReplicatedPages,
  type SlotShareLink,
} from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useMySlots } from '../lib/hooks/use-my-slots';
import { DownloadableQr } from '../components/downloadable-qr';

export function InviteFriendsPage() {
  const storeUser = useAuthStore((s) => s.user);
  const [copied, setCopied] = useState(false);

  // Re-fetch /me on mount so the referral code is correct on first login even
  // if the in-memory session was hydrated before it was available — no manual
  // page refresh needed. Falls back to the store while loading.
  const meQuery = useQuery({ queryKey: ['me'], queryFn: getMe, staleTime: 60_000 });
  const referralCode = meQuery.data?.referralCode ?? storeUser?.referralCode ?? null;

  // The value to embed in the link honors `replicated_link_ref_encrypted`:
  // `shareRef` is the encrypted opaque token when on, else the plaintext code.
  const refQuery = useQuery({
    queryKey: ['referral-code'],
    queryFn: getReferralCode,
    staleTime: 60_000,
  });

  // A multi-slot member shares a per-SLOT link so the slot they're actively
  // working gets credited. Resolve the active slot's own shareRef/code and fall
  // back to the account-level referral code (free slots / single-code tenants /
  // not-yet-loaded keep the page working exactly as before).
  const { activeSlot } = useMySlots();
  const slotLinksQuery = useQuery({
    queryKey: ['slot-share-links'],
    queryFn: getSlotShareLinks,
    staleTime: 60_000,
  });
  const slotLinks = useMemo(() => slotLinksQuery.data ?? [], [slotLinksQuery.data]);
  // ONE page-level choice drives EVERY link on this page — the replicated link,
  // its QR, the campaign links and the store link. The store link used to keep
  // its own private selector while the others silently followed the global
  // sidebar slot, so a member could change "which slot earns" for their store
  // link and not for the link right above it.
  //
  // Deliberately LOCAL state, NOT `useActiveSlotStore`: that store is
  // localStorage-backed and drives catalog PRICING on every price-bearing
  // request, so writing to it from here would silently re-price the storefront
  // as a side effect of picking a link.
  const [pickedSlotId, setPickedSlotId] = useState<string | null>(null);
  const activeLink =
    slotLinks.find((l) => l.slotId === pickedSlotId) ??
    slotLinks.find((l) => l.slotId === activeSlot?.id) ??
    slotLinks[0] ??
    null;
  const shareRef = activeLink?.shareRef ?? refQuery.data?.shareRef ?? referralCode;
  const displayCode = activeLink?.customCode ?? activeLink?.slotCode ?? referralCode;

  // When the tenant nominated a LANDING page, the main link points at `/r/:code`
  // (which renders that page, then routes to signup carrying the code). With no
  // landing page nominated this comes back disabled and the link is the legacy
  // straight-to-signup one — which is the default, not an error state.
  const replicatedQuery = useQuery({
    queryKey: ['replicated-page'],
    queryFn: () => getReplicatedPage(),
  });
  const useCustomPage = replicatedQuery.data?.enabled ?? false;

  // The tenant's named campaign pages (each shareable as /r/<ref>/<slug>).
  const pagesQuery = useQuery({
    queryKey: ['replicated-pages'],
    queryFn: listReplicatedPages,
    staleTime: 60_000,
  });
  const campaignPages = pagesQuery.data?.pages ?? [];
  // Campaign links stand on the MASTER switch, not on whether a landing page is
  // nominated. Gating them on `useCustomPage` meant a tenant that (rightly) sent
  // its bare `/r/<code>` to the registration form silently lost every campaign
  // link from this page — the two are independent choices.
  const campaignsAvailable = pagesQuery.data?.enabled ?? false;

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const link = useMemo(() => {
    if (!shareRef) return '';
    return useCustomPage
      ? `${origin}/r/${encodeURIComponent(shareRef)}`
      : `${origin}/signup?ref=${encodeURIComponent(shareRef)}`;
  }, [shareRef, useCustomPage, origin]);
  const showCampaigns = campaignsAvailable && !!shareRef && campaignPages.length > 0;

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  }

  // The activation kits a lead can reach through this member's link — the id →
  // name lookup the Campaign links card below uses to annotate each tagged
  // campaign. Narrowing itself lives on the campaign page, not here.
  const tenant = useQuery({ queryKey: ['tenant-config'], queryFn: getTenantConfig });
  // Campaign tags only bind when the tenant flag is on; with it off the server
  // ignores them, so annotating a campaign as narrowed would be a lie.
  const campaignScopingOn = tenant.data?.packageReplicatedLinkEnabled ?? true;
  const packageLinksQuery = useQuery({
    // Keyed by SLOT — the package links follow the picker like everything else.
    queryKey: ['package-links', activeLink?.slotId ?? null],
    queryFn: () => getPackageLinks(activeLink?.slotId),
    staleTime: 60_000,
  });
  const packages = packageLinksQuery.data?.packages ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-h1 flex items-center gap-2">
          <Share2 className="h-6 w-6 text-primary" /> Invite Friends
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Share your replicated link. Anyone who signs up through it becomes your affiliate.
        </p>
      </div>

      {!referralCode ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            {meQuery.isLoading
              ? 'Loading your replicated link…'
              : "Your referral code hasn't been generated yet."}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* ONE selector for every link on the page. Only shown to a member who
              actually has a choice to make. */}
          {slotLinks.length > 1 && (
            <Card>
              <CardContent className="flex flex-wrap items-center gap-3 p-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium">Which slot earns from these links?</p>
                  <p className="text-xs text-muted-foreground">
                    Applies to every link on this page.
                  </p>
                </div>
                <select
                  className="ml-auto h-9 rounded-md border bg-background px-3 text-sm"
                  value={activeLink?.slotId ?? ''}
                  onChange={(e) => setPickedSlotId(e.target.value)}
                >
                  {slotLinks.map((s) => (
                    <option key={s.slotId} value={s.slotId}>
                      {s.slotCode} · {s.membershipName}
                      {s.slotType === 'free' ? ' · free' : ''}
                    </option>
                  ))}
                </select>
                {tenant.data?.linkSponsorOverrideActive ? (
                  // Without this the picker reads as a lie on such a tenant: the
                  // member changes "which slot earns" and every signup still
                  // lands somewhere else. Purchases DO follow the pick, so the
                  // wording has to separate the two.
                  <p className="w-full text-xs text-muted-foreground">
                    Note: new signups through your links are assigned by the company to a fixed
                    slot, so this choice affects purchase earnings rather than where sign-ups land.
                  </p>
                ) : null}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Your replicated link</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input readOnly value={link} className="font-mono text-xs" />
                <Button onClick={copy} variant="outline" size="sm">
                  {copied ? (
                    <Check className="h-4 w-4 text-success" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <div className="text-xs text-muted-foreground">
                Affiliate Code: <span className="font-mono">{displayCode}</span>
                <Link
                  to={
                    activeLink?.slotId
                      ? `/settings?tab=referral&slot=${activeLink.slotId}`
                      : '/settings?tab=referral'
                  }
                  className="ml-2 text-primary hover:underline"
                >
                  Change
                </Link>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">QR code</CardTitle>
            </CardHeader>
            <CardContent>
              <DownloadableQr
                value={link}
                fileName={`referral-${displayCode ?? 'link'}`}
                caption="Scan with a phone camera to open the signup page."
              />
            </CardContent>
          </Card>

          {/* Store-wide Share & Earn (flag-gated). Sourced from the per-SLOT
              share links, NOT the account referral code the rest of this page
              falls back to — dropship margin is credited to a specific slot,
              and a free slot can't earn it. */}
          {(tenant.data?.storeWideDropshipLink ?? false) &&
            (tenant.data?.dropshippingIncome ?? false) && <StoreLinkCard chosen={activeLink} />}

          {showCampaigns && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Campaign links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Share a specific landing page. Every link still credits your affiliate network.
                </p>
                <ul className="space-y-2">
                  {campaignPages.map((p) => (
                    <CampaignRow
                      key={p.slug}
                      title={p.title}
                      isDefault={p.isDefault}
                      link={`${origin}/r/${encodeURIComponent(shareRef!)}/${encodeURIComponent(p.slug)}`}
                      // Which activation kits this campaign narrows to. null =
                      // no narrowing: the campaign tags nothing, or the tenant
                      // has campaign scoping switched off entirely.
                      packageNames={
                        !campaignScopingOn || p.kitProductIds.length === 0
                          ? null
                          : packages
                              .filter((pkg) => p.kitProductIds.includes(pkg.kitProductId))
                              .map((pkg) => pkg.kitName)
                      }
                    />
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Store-wide Share & Earn card: `/s/<slot shareRef>` drops a lead on the
 * storefront and everything they buy in that visit pays this slot the retail
 * margin — same terms as a per-product Share & Earn link.
 */
function StoreLinkCard({ chosen }: { chosen: SlotShareLink | null }) {
  // No selector of its own any more: the page owns the choice, so this card and
  // the replicated link above it can no longer point at different slots.
  const [copied, setCopied] = useState(false);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const link = chosen ? storeShareLink(origin, chosen.shareRef) : '';

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Store link (Share &amp; Earn)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Share the whole store. Anything a lead buys through this link earns you the retail margin,
          just like a product Share &amp; Earn link.
        </p>
        {!chosen ? (
          <p className="text-sm text-muted-foreground">
            You need an active slot to earn from a store link.
          </p>
        ) : chosen.slotType === 'free' ? (
          // A free slot can refer, but `POST /storefront/dropship-orders`
          // rejects it as the referrer (`referrer_slot_not_earning`). Showing
          // the link anyway would be handing the member a link that 400s at
          // checkout.
          <p className="text-sm text-muted-foreground">
            {chosen.slotCode} is a free slot, which can&rsquo;t earn store margin. Pick a paid slot
            above to get a store link.
          </p>
        ) : (
          <>
            <div className="flex gap-2">
              <Input readOnly value={link} className="font-mono text-xs" />
              <Button onClick={copy} variant="outline" size="sm">
                {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <DownloadableQr
              value={link}
              fileName={`store-${chosen.customCode ?? chosen.slotCode}`}
              caption="Scan to open the store — you earn the margin on every sale."
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** One campaign page's shareable link with its own copy button. */
function CampaignRow({
  title,
  isDefault,
  link,
  packageNames,
}: {
  title: string;
  isDefault: boolean;
  link: string;
  // null = campaign is untagged (unlocks all the member's packages);
  // [] = tagged but none overlap the member's tier (lead can buy nothing).
  /** Kits this campaign narrows to; null = no narrowing (untagged, or scoping off). */
  packageNames: string[] | null;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  }
  return (
    <li className="space-y-1 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {title}
          {isDefault && (
            <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
              Landing page
            </span>
          )}
        </span>
        <Button onClick={copy} variant="outline" size="sm">
          {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <Input readOnly value={link} className="font-mono text-xs" />
      <p className="text-[11px] text-muted-foreground">
        {packageNames === null
          ? 'Unlocks all your packages'
          : packageNames.length
            ? `Package${packageNames.length > 1 ? 's' : ''}: ${packageNames.join(', ')}`
            : 'No available package tagged'}
      </p>
    </li>
  );
}
